import { api } from "../api";

export interface Role {
  id: string;
  tenantId: string | null;
  name: string;
  description?: string | null;
  permissions: string[];
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRoleDto {
  name: string;
  description?: string;
  permissions: string[];
}

export interface UpdateRoleDto {
  name?: string;
  description?: string;
  permissions?: string[];
}

export interface PermissionGroup {
  label: string;
  permissions: Array<{
    key: string;
    label: string;
    description?: string;
  }>;
}

export interface PermissionCatalog {
  groups: PermissionGroup[];
}

export const rolesApi = {
  getAll: async (): Promise<Role[]> => {
    const response = await api.get("/roles");
    return response.data;
  },

  getById: async (id: string): Promise<Role> => {
    const response = await api.get(`/roles/${id}`);
    return response.data;
  },

  getPermissionCatalog: async (): Promise<PermissionCatalog> => {
    const response = await api.get("/roles/permissions/catalog");
    const backendGroups = response.data.groups || [];

    // Backend returns permissions with key, label, description per permission
    const transformedGroups: PermissionGroup[] = backendGroups.map((group: any) => {
      if (group.permissions && Array.isArray(group.permissions)) {
        return {
          label: group.label,
          permissions: group.permissions.map((p: any) => ({
            key: p.key,
            label: p.label || p.key,
            description: p.description,
          })),
        };
      }
      // Fallback for old format (keys only)
      const keys = group.keys || [];
      return {
        label: group.label,
        permissions: keys.map((key: string) => {
          const label = key
            .split(/[.:]/)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join(" ");
          return { key, label, description: group.description };
        }),
      };
    });

    return { groups: transformedGroups };
  },

  create: async (data: CreateRoleDto, reAuthPassword?: string): Promise<Role> => {
    const response = await api.post("/roles", data, {
      headers: reAuthPassword ? { "X-Re-Auth-Password": reAuthPassword } : {},
    });
    return response.data;
  },

  update: async (id: string, data: UpdateRoleDto, reAuthPassword?: string): Promise<Role> => {
    const response = await api.patch(`/roles/${id}`, data, {
      headers: reAuthPassword ? { "X-Re-Auth-Password": reAuthPassword } : {},
    });
    return response.data;
  },

  delete: async (id: string, reAuthPassword?: string): Promise<void> => {
    await api.delete(`/roles/${id}`, {
      headers: reAuthPassword ? { "X-Re-Auth-Password": reAuthPassword } : {},
    });
  },

  /** Refresh default system roles with latest permissions (e.g. Manage Invoices). */
  refreshDefaultRoles: async (): Promise<{ message: string; updated: boolean }> => {
    const response = await api.post("/roles/refresh-default-roles");
    return response.data;
  },
};
